import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { IosInstallPrompt } from "./components/IosInstallPrompt";
import { AuthProvider, useAuth } from "./lib/auth";
import { LandingPage } from "./pages/LandingPage";

const CostsPage = lazy(() =>
  import("./pages/CostsPage").then(({ CostsPage }) => ({ default: CostsPage })),
);
const LibraryPage = lazy(() =>
  import("./pages/LibraryPage").then(({ LibraryPage }) => ({ default: LibraryPage })),
);
const NewSummaryPage = lazy(() =>
  import("./pages/NewSummaryPage").then(({ NewSummaryPage }) => ({ default: NewSummaryPage })),
);
const TasksPage = lazy(() =>
  import("./pages/TasksPage").then(({ TasksPage }) => ({ default: TasksPage })),
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then(({ NotFoundPage }) => ({ default: NotFoundPage })),
);
const SharedSummaryPage = lazy(() =>
  import("./pages/SharedSummaryPage").then(({ SharedSummaryPage }) => ({
    default: SharedSummaryPage
  })),
);
const UserTimeSavedPage = lazy(() =>
  import("./pages/UserTimeSavedPage").then(({ UserTimeSavedPage }) => ({
    default: UserTimeSavedPage
  })),
);

export default function App() {
  return (
    <Routes>
      {/* Share links resolve outside AuthProvider on purpose. The recipient is
          expected to have no account, so mounting the auth layer here would
          bounce them to the landing page — and would make them wait on the
          LIFF SDK before any text appeared. */}
      <Route
        path="/s/:token"
        element={
          <Suspense
            fallback={
              <div className="app-loading" role="status">
                กำลังเปิดบทสรุป…
              </div>
            }
          >
            <SharedSummaryPage />
          </Suspense>
        }
      />
      {/* Public time-saved page — also outside AuthProvider: the visitor is a
          stranger being shown a number, not an account holder. */}
      <Route
        path="/u/:token"
        element={
          <Suspense
            fallback={
              <div className="app-loading" role="status">
                กำลังเปิดหน้านี้…
              </div>
            }
          >
            <UserTimeSavedPage />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={
          <AuthProvider>
            <AuthedApp />
          </AuthProvider>
        }
      />
    </Routes>
  );
}

function AuthedApp() {
  const { status } = useAuth();

  if (status === "loading") {
    return (
      <div className="app-loading" role="status">
        กำลังเข้าสู่ระบบ…
      </div>
    );
  }

  if (status === "unauthed") {
    return <LandingPage />;
  }

  return (
    <>
      <Suspense
        fallback={
          <div className="app-loading" role="status">
            กำลังเปิดพื้นที่ทำงาน…
          </div>
        }
      >
        <Routes>
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/new" replace />} />
            <Route path="new" element={<NewSummaryPage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="library/:guideId" element={<LibraryPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="costs" element={<CostsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
      <IosInstallPrompt />
    </>
  );
}
